import React, { useCallback, useEffect, useState } from 'react';

import { DataClassifiers } from '@ariksa/data-scanning/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  ActionButton,
  AddButton,
  ButtonGroup,
  ButtonGroupItem,
} from 'components/DataEntry';
import {
  CheckmarkCircleOutlineIcon,
  DisableIcon,
  EditIcon,
  IconTypes,
  TrashIcon,
} from 'components/Icons';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { DataClassifierModal } from 'containers/Setup/DataConfiguration/Components/DataClassifier/DataClassifierModal';
import { EditDataClassifier } from 'containers/Setup/DataConfiguration/Components/DataClassifier/DataClassifierWizard/EditDataClassifier';
import { useDataClassifierTableColumns } from 'containers/Setup/DataConfiguration/Components/DataClassifier/hooks/useDataClassifierTableColumns';
import { dataConfigurationViewByOptions } from 'containers/Setup/DataConfiguration/Components/DataClassifier/utils';
import { selectDataClassification } from 'containers/Setup/DataConfiguration/selector';
import { actions } from 'containers/Setup/DataConfiguration/slice';
import { DataClassifierAction } from 'containers/Setup/DataConfiguration/types';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

export const DataClassifierTable: React.FC = () => {
  const dispatch = useDispatch();
  const { dataClassifiers } = useSelector(selectDataClassification);
  const action = useDisclosure();
  const navigate = useNavigate();
  const [currentRecord, setCurrentRecord] = useState<DataClassifiers>(
    {} as DataClassifiers,
  );
  const params = useSearchParams<{ viewBy: string }>();
  const [
    classifierAction,
    setClassifierAction,
  ] = useState<DataClassifierAction>('Enable');
  const {
    dataExclusionColumns,
    dataTypeColumns,
  } = useDataClassifierTableColumns();
  const [selectedTab, setSelectedTab] = useState<
    ButtonGroupItem & Record<string, any>
  >(dataConfigurationViewByOptions[0]);
  const editConfiguration = useDisclosure();
  const { updateValueInURL } = useSearchParamFunctions();

  useEffect(() => {
    if (!!params.viewBy) {
      if (params.viewBy === 'exclusions')
        setSelectedTab(dataConfigurationViewByOptions[1]);
      else setSelectedTab(dataConfigurationViewByOptions[0]);
    }
  }, [params?.viewBy]);

  const getData = useCallback(() => {
    params?.viewBy === 'exclusions'
      ? dispatch(actions.getDataExclusions({ q: {} }))
      : dispatch(actions.getDataTypes({ q: {} }));
  }, [dispatch, params?.viewBy]);

  useDeepCompareEffect(() => {
    getData();
  }, [getData]);

  const renderAction = ({ row }) => {
    return (
      <Center
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <ActionButton
          label="Edit"
          icon={<EditIcon />}
          onClick={() => {
            editConfiguration.onOpen();
            setCurrentRecord(row);
          }}
        />
        {selectedTab?.value !== 'types' && (
          <ActionButton
            label={row?.is_enabled ? 'Disable' : 'Enable'}
            icon={
              !row?.is_enabled ? (
                <CheckmarkCircleOutlineIcon color="green" />
              ) : (
                <DisableIcon color="red" />
              )
            }
            isDisabled={row?.created_by === 'Ariksa'}
            onClick={() => {
              setCurrentRecord(row);
              action.onOpen();
              setClassifierAction(row?.is_enabled ? 'Disable' : 'Enable');
            }}
          />
        )}
        <ActionButton
          label={'Delete'}
          icon={<TrashIcon color="red" />}
          isDisabled={row?.created_by === 'Ariksa'}
          onClick={() => {
            setCurrentRecord(row);
            action.onOpen();
            setClassifierAction('Delete');
          }}
        />
      </Center>
    );
  };

  const columns = [
    ...(selectedTab?.value === 'types'
      ? dataTypeColumns
      : dataExclusionColumns),
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
    },
  ];

  return editConfiguration.isOpen ? (
    <EditDataClassifier
      classifier={currentRecord}
      onClose={editConfiguration.onClose}
    />
  ) : (
    <Card>
      <Stack w="full" spacing={5}>
        <Box>
          <PageHeaderWithIcon
            label="Data Configuration"
            iconType={IconTypes.Configuration}
          />
        </Box>

        <HStack justify="space-between" w="full">
          <HStack>
            <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
            <ButtonGroup
              items={dataConfigurationViewByOptions}
              value={selectedTab}
              onClick={tab => {
                updateValueInURL('viewBy', tab.value);
              }}
            />
          </HStack>
          <AddButton
            label={'Add Data ' + toTitleCase(selectedTab?.value)}
            onClick={() => {
              navigate('/setup/data/add/' + selectedTab?.value);
            }}
          />
        </HStack>
        <Box w="full" h="full" flex={1}>
          <Table
            data={dataClassifiers.data}
            columns={columns}
            isLoading={dataClassifiers.isLoading}
          />
        </Box>
        {action.isOpen && (
          <DataClassifierModal
            currentRecord={currentRecord}
            isOpen={action.isOpen}
            onClose={action.onClose}
            actionType={classifierAction}
            selectedTab={selectedTab?.value}
          />
        )}
      </Stack>
    </Card>
  );
};
